import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Photo, Button } from '../../../components'
import { useMessage, useGlobalState, useModalDialog } from '../../../hooks'
import { IconBuddy } from '../components'
import { generationService, statsService } from '../services'
import { useBuddyDialog } from '../hooks'
import { questionnaireService } from '../../feedback/services'
import '../index.css'

// The buddy-checkin questionnaire is looked up by name, not id — ids aren't
// guaranteed to match across deployments/DB seeds, but this exact name is
// reserved for this purpose.
const FEEDBACK_QUESTIONNAIRE_NAME = 'Buddy - Uppföljning'

const Home = () => {
  const navigate = useNavigate()
  const { showMessage } = useMessage()
  const { hideModalDialog } = useModalDialog()
  const { showEditDatesDialog } = useBuddyDialog()
  const [loggedInUser] = useGlobalState('loggedInUser')

  const [latestRound, setLatestRound] = useState(null)
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [claimingUserId, setClaimingUserId] = useState(null)
  const [ejectingPairId, setEjectingPairId] = useState(null)
  const [sendingFeedbackFor, setSendingFeedbackFor] = useState(null)
  const [engagement, setEngagement] = useState(null)

  const loadEngagement = useCallback((signal) => {
    return statsService.getEngagement(signal).then(setEngagement)
  }, [])

  useEffect(() => {
    const ctrl = new AbortController()
    Promise.all([
      generationService.getHistory({ deleted: false, page: 1, pageSize: 1 }, ctrl.signal).then(res => setLatestRound(res.generations[0] || null)),
      generationService.getUsers(ctrl.signal).then(res => setUsers(res.users || [])),
      loadEngagement(ctrl.signal),
    ])
      .then(() => setLoading(false))
      .catch(err => { if (err.name !== 'AbortError') { showMessage('Kunde inte ladda sessionen', 'error'); setLoading(false) } })
    return () => ctrl.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Looks up the reserved questionnaire by name (never by id — see
  // FEEDBACK_QUESTIONNAIRE_NAME) and only navigates if it's actually found;
  // on any error (not found, network, etc.) this shows the error and does
  // nothing else.
  const handleFeedbackClick = (buddy) => {
    setSendingFeedbackFor(buddy.id)
    questionnaireService.getByName(FEEDBACK_QUESTIONNAIRE_NAME)
      .then(res => navigate(`/feedback/requests/give?user_id=${buddy.id}&questionnaire_id=${res.questionnaire.questionnaire_id}`))
      .catch(err => showMessage(err.message || `Kunde inte hitta formuläret "${FEEDBACK_QUESTIONNAIRE_NAME}"`, 'error'))
      .finally(() => setSendingFeedbackFor(null))
  }

  const myPairs = (latestRound?.pairs || []).filter(p => p.user_id_a === loggedInUser.user_id || p.user_id_b === loggedInUser.user_id)
  const myBuddies = myPairs.map(p => p.user_id_a === loggedInUser.user_id
    ? { id: p.user_id_b, name: p.display_name_b, photo: p.photo_b }
    : { id: p.user_id_a, name: p.display_name_a, photo: p.photo_a })

  // Anyone eligible with no pair in the current round — a new joiner,
  // someone left without a buddy after their old one was removed, or
  // someone who was deliberately excluded this round but wants to join in
  // after all (being excluded doesn't block a claim, only having a pair does).
  // Excludes the logged-in user themselves — you can't become buddies with
  // yourself, so if you're currently unclaimed too you shouldn't see
  // yourself listed as someone to claim (the backend rejects it either way).
  const pairedUserIds = new Set((latestRound?.pairs || []).flatMap(p => [p.user_id_a, p.user_id_b]))
  const unclaimedUsers = latestRound ? users.filter(u => !pairedUserIds.has(u.user_id) && u.user_id !== loggedInUser.user_id) : []

  const hasMegaBuddy = !!latestRound?.mega_buddy_user_id
  const myPairWithMegaBuddy = hasMegaBuddy && myPairs.find(p => p.user_id_a === latestRound.mega_buddy_user_id || p.user_id_b === latestRound.mega_buddy_user_id)
  const amMegaBuddy = latestRound?.mega_buddy_user_id === loggedInUser.user_id
  const amMegaBuddysBuddy = !!myPairWithMegaBuddy && !amMegaBuddy

  // Anyone who isn't the mega buddy can claim an unclaimed person — whether
  // that means pairing up directly (if you have no buddy yourself) or
  // extending your own pair into a trio (the backend only allows the latter
  // while there isn't a different mega buddy already this round). The mega
  // buddy's own two buddies get a second option: break loose to take the
  // new person instead, dissolving the trio.
  const canClaim = !amMegaBuddy
  const canBreakLoose = hasMegaBuddy && amMegaBuddysBuddy

  const handleClaim = (unclaimedUserId) => {
    setClaimingUserId(unclaimedUserId)
    const action = canBreakLoose ? generationService.breakLoose : generationService.claim
    action(unclaimedUserId)
      .then(res => {
        setLatestRound(res.generation)
        showMessage('Klart! Ni är nu buddies.')
        // The pair count just changed, so the engagement denominator
        // (expected/actual) is stale until this reloads too.
        return loadEngagement()
      })
      .catch(err => showMessage(err.message || 'Kunde inte bli buddy', 'error'))
      .finally(() => setClaimingUserId(null))
  }

  // Dissolves one specific pairing — not a person, since a mega buddy has
  // two separate pairings and ejecting one must leave the other (and that
  // partner) untouched. Whoever's left with no pairing at all shows up in
  // "Väntar på en buddy" right after, ready to be claimed again by anyone.
  const handleEject = (generationPairId) => {
    setEjectingPairId(generationPairId)
    generationService.eject(generationPairId)
      .then(res => {
        setLatestRound(res.generation)
        // res.generation is null once the very last pairing is ejected — the
        // now-empty round is deleted outright rather than kept as a 0-pair
        // husk, so the only way forward is generating a fresh one.
        showMessage(res.generation
          ? 'Buddyn är nu tillgänglig att bli vald igen.'
          : 'Sista paret borttaget — rundan är nu borttagen. Generera en ny runda.')
        // The pair count just changed (one pairing dissolved), so the
        // engagement denominator (expected/actual) is stale until this
        // reloads too.
        return loadEngagement()
      })
      .catch(err => showMessage(err.message || 'Kunde inte skicka ut buddyn', 'error'))
      .finally(() => setEjectingPairId(null))
  }

  const handleUpdateDates = (dates) => {
    generationService.updateDates(latestRound.generation_id, dates)
      .then(res => { setLatestRound(res.generation); hideModalDialog(); showMessage('Datum uppdaterat.') })
      .catch(err => showMessage(err.message || 'Kunde inte uppdatera datum', 'error'))
  }

  const openEditDates = () => showEditDatesDialog(latestRound, handleUpdateDates)

  return (
    <div className="bud-home">

      <div className="bud-home-top">

        <div className="bud-home-card">
          <h2>Feedback</h2>
          {myBuddies.length === 0
            ? <p className="bud-home-card-empty">Du har ingen buddy just nu.</p>
            : (
              <div className="bud-feedback-buttons">
                {myBuddies.map(buddy => (
                  <button
                    type="button"
                    className="bud-feedback-button"
                    key={buddy.id}
                    disabled={sendingFeedbackFor === buddy.id}
                    onClick={() => handleFeedbackClick(buddy)}
                  >
                    Hur mår <Photo data={buddy.photo} alt={buddy.name} tooltip={buddy.name} size="small" />?
                  </button>
                ))}
              </div>
            )
          }
        </div>

        <div className="bud-home-card">
          <h2><IconBuddy name="stats" /> Statistik</h2>
          {loading || !engagement
            ? <p className="bud-home-card-empty">Laddar…</p>
            : !engagement.expected
              ? <p className="bud-home-card-empty">Ingen aktiv session än.</p>
              : engagement.questionnaireMissing
                ? <p className="bud-home-card-empty">Formuläret "Buddy - Uppföljning" saknas.</p>
                : (
                  <div className="bud-stats-row">
                    <div className="bud-stat">
                      <div className="bud-stat-value">{engagement.percentage}%</div>
                      <div className="bud-stat-label">
                        Feedback-engagemang denna vecka
                        <div className="bud-stat-detail">{engagement.actual} av {engagement.expected} inlämnade</div>
                      </div>
                    </div>
                    <div className="bud-stat">
                      <div className="bud-stat-value">{engagement.roundReviewCount}</div>
                      <div className="bud-stat-label">Recensioner inlämnade i denna runda</div>
                    </div>
                  </div>
                )
          }
        </div>

        {!loading && unclaimedUsers.length > 0 && (canClaim || canBreakLoose) &&
          <div className="bud-home-card">
            <h2>Väntar på en buddy</h2>
            <div className="bud-claim-list">
              {unclaimedUsers.map(u => (
                <div className="bud-claim-row" key={u.user_id}>
                  <Photo data={u.photo} alt={u.name} tooltip={u.name} size="small" />
                  <span className="bud-claim-name">{u.name}</span>
                  <Button
                    value={claimingUserId === u.user_id ? 'Bli buddy...' : 'Bli buddy'}
                    className="button main-button bud-claim-button"
                    disabled={claimingUserId === u.user_id}
                    onClick={() => handleClaim(u.user_id)}
                  />
                </div>
              ))}
            </div>
          </div>
        }

      </div>

      <div className="bud-home-bottom">
        <div className="bud-home-card">
          <h2>Nuvarande runda</h2>
          {!loading && latestRound &&
            <p className="bud-round-dates">
              Startade {new Date(latestRound.generation_timestamp).toLocaleDateString('sv-SE')}
              {latestRound.round_end_timestamp && <> — slutar {new Date(latestRound.round_end_timestamp).toLocaleDateString('sv-SE')}</>}
              <button type="button" className="icon-edit bud-edit-dates-btn" onClick={openEditDates} title="Ändra datum" />
            </p>
          }

          {loading
            ? <p className="bud-home-card-empty">Laddar…</p>
            : !latestRound
              ? (
                <div className="bud-home-generate-empty">
                  <Button value="+ generera runda" className="button main-button" onClick={() => navigate('/buddy/generate')} />
                </div>
              )
              : (
                <table className="bud-table">
                  <thead>
                    <tr>
                      <th>Buddy 1</th>
                      <th>Buddy 2</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {latestRound.pairs.map((pair, i) => (
                      <tr key={i}>
                        <td>
                          <span className={"bud-table-name" + (pair.user_id_a === loggedInUser.user_id ? " bud-table-you" : "")}>{pair.display_name_a}</span>
                          {latestRound.mega_buddy_user_id === pair.user_id_a && <span className="pill bud-pill-mega">Mega-buddy</span>}
                        </td>
                        <td>
                          <span className={"bud-table-name" + (pair.user_id_b === loggedInUser.user_id ? " bud-table-you" : "")}>{pair.display_name_b}</span>
                          {latestRound.mega_buddy_user_id === pair.user_id_b && <span className="pill bud-pill-mega">Mega-buddy</span>}
                        </td>
                        <td className="bud-table-actions">
                          <button
                            type="button"
                            className="icon-remove bud-eject-btn"
                            disabled={ejectingPairId === pair.generation_pair_id}
                            onClick={() => handleEject(pair.generation_pair_id)}
                            title="Skicka ut för att göra tillgänglig igen"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
          }
        </div>
      </div>

    </div>
  )
}

export default Home
